import { QuestRepeatType } from "@playcharge/common/models/backend/quests/quest-repeat-type";
import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatButton } from "@angular/material/button";
import {
    MatDialogActions,
    MatDialogContent,
    MatDialogRef,
    MatDialogTitle,
} from "@angular/material/dialog";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatOption, MatSelect } from "@angular/material/select";
import { EnumKeyValuePipe } from "../../../../pipes/enumKeyValue.pipe";
import {
    MatChipGrid,
    MatChipInput,
    MatChipInputEvent,
    MatChipRemove,
    MatChipRow,
} from "@angular/material/chips";
import { MatIcon } from "@angular/material/icon";
import { Nullable } from "@playcharge/common/models/backend/nullable";
import { QuestScheduleCreateModel } from "@playcharge/common/models/backend/quests/backoffice/quest-schedule-create-model";

@Component({
    selector: "app-quest-schedule-create-dialog",
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        MatFormField,
        MatLabel,
        MatInput,
        MatSelect,
        MatOption,
        MatChipGrid,
        MatChipRow,
        MatChipRemove,
        MatIcon,
        MatChipInput,
        EnumKeyValuePipe,
        MatButton,
    ],
    templateUrl: "./quest-schedule-create-dialog.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestScheduleCreateDialogComponent {
    private _matDialogRef: MatDialogRef<
        QuestScheduleCreateDialogComponent,
        QuestScheduleCreateModel
    > = inject(MatDialogRef<QuestScheduleCreateDialogComponent, QuestScheduleCreateModel>);

    public form: FormGroup<{
        name: FormControl<string>;
        repeat: FormControl<QuestRepeatType>;
        retentionDays: FormControl<Nullable<number[]>>;
    }> = new FormGroup({
        name: new FormControl<string>("", { nonNullable: true, validators: [Validators.required] }),
        repeat: new FormControl<QuestRepeatType>(QuestRepeatType.Daily, {
            nonNullable: true,
            validators: [Validators.required],
        }),
        retentionDays: new FormControl<Nullable<number[]>>(
            Array.from({ length: 30 }, (_, index) => index),
        ),
    });

    public QuestRepeatType: typeof QuestRepeatType = QuestRepeatType;

    public addRetentionDay(event: MatChipInputEvent): void {
        const value: number = Number(event.value.trim());

        if (isNaN(value)) {
            return;
        }

        const control: FormControl = this.retentionDaysControl;

        if (control.value) {
            control.patchValue([...control.value, value]);
        } else {
            control.patchValue([value]);
        }

        event.chipInput.clear();
    }

    public removeRetentionDay(retentionDay: number): void {
        const control: FormControl = this.retentionDaysControl;

        if (control.value) {
            const index = control.value.indexOf(retentionDay);

            if (index >= 0) {
                control.patchValue([
                    ...control.value.slice(0, index),
                    ...control.value.slice(index + 1),
                ]);
            }
        }
    }

    public close(): void {
        this._matDialogRef.close(this.form.getRawValue());
    }

    private get retentionDaysControl(): FormControl {
        return this.form.controls.retentionDays;
    }
}
